import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useIonRouter } from '@ionic/react';
import { EmptyChatModalFeature, FirstChatMessageModalFeature, Loading } from '@mindly/ui-components';
import {
  chatErrorSelector,
  isEmptyModalOpenSelector,
  isLoadingSelector,
  sendMessageToSelector,
} from 'src/redux/chat/selector';
import { closeEmptyChatModal, disableStreamChat, enableStreamChat, setSendMessageTo } from 'src/redux/chat/reducer';
import { psychologistSelectorById } from 'src/redux/specialists/selector';
import { PrivateRoutes } from 'src/routes/navigation';
import { sendChatMessage } from 'src/redux/chat/thunk';
import useToast from 'src/context/ToastContext/ToastContext';
import { useIsKeyBoardShown } from 'src/shared';
import { App } from '@capacitor/app';
import { useRemoteConfig } from 'src/hooks/useRemoteConfig/useRemoteConfig';

const ChatControlsWidget = () => {
  const [isFocus, setIsFocus] = useState(false);

  const isEmptyModalOpen = useSelector(isEmptyModalOpenSelector);
  const sendMessageTo = useSelector(sendMessageToSelector);
  const specialist = useSelector(psychologistSelectorById(sendMessageTo?.id ?? ''));
  const isLoading = useSelector(isLoadingSelector);
  const chatError = useSelector(chatErrorSelector);

  const { t } = useTranslation('chat');
  const dispatch = useDispatch();
  const navigation = useIonRouter();
  const { showToast } = useToast();
  const isKeyboardShown = useIsKeyBoardShown();
  const { isActiveFeature } = useRemoteConfig();

  useEffect(() => {
    checkIsStreamChatEnabled();

    App.addListener('appStateChange', async ({ isActive }) => {
      if (isActive) {
        checkIsStreamChatEnabled();
      }
    });

    return () => {
      App.removeAllListeners();
    };
  }, []);

  const checkIsStreamChatEnabled = async () => {
    const isStreamChatEnabled = await isActiveFeature('is_stream_chat_enabled');
    dispatch(isStreamChatEnabled ? enableStreamChat() : disableStreamChat());
  };

  useEffect(() => {
    if (chatError) {
      showToast({ text: chatError });
    }
  }, [chatError]);

  const onEmptyModalClose = () => {
    dispatch(closeEmptyChatModal());
  };

  const onSendMessageModalClose = () => {
    dispatch(setSendMessageTo(null));
    setIsFocus(false);
  };

  const onSendMessageModalOpen = () => {
    setIsFocus(true);
  };

  const onFindSpecialistButtonClick = () => {
    dispatch(closeEmptyChatModal());
    navigation.push(PrivateRoutes.PSYCHOLOGIST_LIST);
  };

  const onMessageSend = async (val: string) => {
    if (!sendMessageTo) return;

    await dispatch(sendChatMessage({ to: sendMessageTo.id, message: val }));

    dispatch(setSendMessageTo(null));
  };

  return (
    <>
      <EmptyChatModalFeature
        isOpen={isEmptyModalOpen}
        title={t('empty_chats.title')}
        subtitle={t('empty_chats.descrirption')}
        buttonTitle={t('empty_chats.button')}
        onButtonClick={onFindSpecialistButtonClick}
        onDidDismiss={onEmptyModalClose}
        isShowHandle={false}
      />
      <FirstChatMessageModalFeature
        title={t('first_message.title', {
          specialistName: `${specialist?.first_name} ${specialist?.last_name?.charAt(0)}.`,
        })}
        avatarUrl={specialist?.avatar_link ?? ''}
        modalProps={{
          isOpen: !!sendMessageTo,
          onDidPresent: onSendMessageModalOpen,
          onDidDismiss: onSendMessageModalClose,
          isShowHandle: false,
        }}
        inputProps={{
          isFocus,
          isKeyboardShown,
          placeholder: t('first_message.hint'),
          maxlength: 3000,
          buttonText: t('first_message.button'),
          onSubmit: onMessageSend,
          isLoading,
        }}
      />
      <Loading isOpen={isLoading} />
    </>
  );
};

export default ChatControlsWidget;
